import React, { useState } from 'react';
import { Card } from './ui/Card';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote } from 'lucide-react';
import { RadiantStar } from './ui/RadiantStar';
type Review = {
  shortText: string;
  longText: string;
  author: string;
  role?: string;
};
const reviews: Review[] = [{
  shortText: 'Mark, your coaching style—structured yet flexible, challenging yet safe—made growth feel achievable and inspired real change in me. With your encouragement and insightful questions, I found the confidence to step out of my comfort zone, build sustainable habits, and shift from supporting to proactively shaping outcomes.',
  longText: 'Mark, your coaching style has been both structured and flexible, creating a safe space where growth felt not only possible but truly achievable. You balanced encouragement with practical challenge, helping me turn big intentions into small, effective habits—like mailbox routines, time-blocking, and setting clear boundaries. Through our role plays and your insightful questions, I gained the confidence to communicate more openly and handle complex team dynamics with greater clarity.\n\nYour unwavering support and positive energy inspired me to step out of my comfort zone and discover new strengths. This coaching journey was genuinely transformative: I shifted from a support role to proactively shaping outcomes, building resilience, and finding renewed energy in my work. I deeply appreciate the trust you built and the honest dialogue we shared. Thank you for guiding me on this path of continuous growth—I look forward to carrying these lessons forward.',
  author: 'Marjut',
  role: '(Finnish)'
}, {
  shortText: "This coaching journey has had a really meaningful impact on me. What I appreciated most was Mark's ability to listen deeply and mirror back my thoughts with clarity — even when they felt messy or emotional. I always felt fully heard, supported, and respected. I'm coming out of this journey feeling more centered, more self-aware, and more equipped to make aligned decisions about my next steps.",
  longText: 'Over the past months, this coaching journey has had a really meaningful impact on me, both professionally and personally. Even though I\'m still in the "baby steps" phase, I now have much more clarity about what I want from my career and the kind of HR roles that truly fit my values, strengths, and long-term goals.\n\nWhat I appreciated most about Mark\'s coaching style was his ability to listen deeply and mirror back what I said. Even when my thoughts felt messy or emotional, he always managed to capture the essence, structure it, and reflect it back in a much clearer way. Hearing my own thoughts through his words gave me perspective and helped me see that some things weren\'t as dramatic as I was making them in my head.\n\nI always felt that he was fully present in our sessions, despite his busy schedule. I never felt rushed or like a "time slot", I felt heard, supported, and respected. The exercise at the end was especially powerful. It helped me surface unconscious beliefs, confirm what truly matters to me, and connect my ideal work and personal life in a way that feels very authentic.\n\nOverall, I\'m coming out of this journey feeling more centered, more self-aware, and more equipped to make aligned decisions about my next steps. Thank you for creating such a safe space for exploration, for challenging me gently when needed, and for helping me translate confusion into clarity and concrete intentions.',
  author: 'Marianna',
  role: '(Colombian)'
}];
export function ReviewsSection() {
  return <section className="relative py-24 overflow-hidden bg-earth-900/30" data-id="element-114">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-400/20 to-transparent" data-id="element-115"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-400/20 to-transparent" data-id="element-116"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-117">
        <div className="text-center mb-16" data-id="element-118">
          <div className="flex justify-center mb-4" data-id="element-119">
            <RadiantStar size={48} animate={true} data-id="element-120" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-cream-50 mb-4" data-id="element-121">
            What Others Have Found
          </h2>
          <p className="text-gold-400 text-lg font-medium tracking-wide uppercase" data-id="element-122">
            Stories of Growth
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-123">
          {reviews.map((review, index) => <ReviewCard key={index} review={review} data-id="element-124" />)}
        </div>
      </div>
    </section>;
}
function ReviewCard({
  review
}: {
  review: Review;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return <div className="relative h-full" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-125">
      <Card className="h-full relative overflow-hidden group cursor-default transition-all duration-300 border-gold-400/20 hover:border-gold-400/40" data-id="element-126">
        {/* Default View (Short Text) */}
        <div className="flex flex-col h-full p-2" data-id="element-127">
          <Quote className="text-gold-400 mb-4 opacity-50" size={32} data-id="element-128" />

          <p className="text-cream-50/90 text-lg leading-relaxed italic mb-6 flex-grow" data-id="element-129">
            "{review.shortText}"
          </p>

          <div className="mt-auto" data-id="element-130">
            <p className="text-gold-400 font-serif text-lg" data-id="element-131">
              — {review.author}{' '}
              <span className="text-gold-400/70 text-base" data-id="element-132">{review.role}</span>
            </p>

            <div className="mt-4 flex items-center justify-center" data-id="element-133">
              <span className="text-xs text-gold-400/60 uppercase tracking-widest border-b border-gold-400/20 pb-0.5 group-hover:text-gold-400 transition-colors" data-id="element-134">
                Hover for full review
              </span>
            </div>
          </div>
        </div>

        {/* Hover Overlay (Long Text) */}
        <AnimatePresence data-id="element-135">
          {isHovered && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="absolute inset-0 bg-night-900/95 backdrop-blur-sm p-8 flex flex-col justify-center overflow-y-auto scrollbar-hide z-20" data-id="element-136">
              <div className="prose prose-invert max-w-none" data-id="element-137">
                {review.longText.split('\n\n').map((paragraph, i) => <p key={i} className="text-cream-50 text-base leading-relaxed mb-4 last:mb-0" data-id="element-138">
                    {paragraph}
                  </p>)}
              </div>

              <div className="mt-6 pt-4 border-t border-gold-400/20" data-id="element-139">
                <p className="text-gold-400 font-serif text-lg" data-id="element-140">
                  — {review.author}{' '}
                  <span className="text-gold-400/70 text-base" data-id="element-141">
                    {review.role}
                  </span>
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Card>
    </div>;
}